import { Edit, Plus, Trash2, X } from 'lucide-react'
import { Badge } from '../ui/badge'
import { Button } from '../ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card'
import { Input } from '../ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select'

export default function DivisionsList({
  divisions,
  divisionsLoading,
  selectedSport,
  isAdmin,
  onOpenAddDivision,
  onOpenEditDivision,
  onOpenDeleteDivision,
  onSetDivisionActive,
  // Add Division Modal
  showAddDivisionModal,
  onCloseAddDivisionModal,
  divisionForm,
  onDivisionFormChange,
  onSaveDivision,
  // Edit Division Modal
  showEditDivisionModal,
  onCloseEditDivisionModal,
  editingDivision,
  // Delete Division Modal
  showDeleteDivisionModal,
  onCloseDeleteDivisionModal,
  deletingDivision,
  onConfirmDeleteDivision,
}) {
  const statusBadge = (isActive) => (
    <Badge variant={isActive ? 'teal' : 'muted'}>{isActive ? 'Active' : 'Disabled'}</Badge>
  )

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <CardTitle className="truncate">
              Divisions{selectedSport ? `: ${selectedSport.name}` : ''}
            </CardTitle>
            {isAdmin && selectedSport && (
              <Button size="sm" onClick={onOpenAddDivision} className="w-full sm:w-auto">
                <Plus className="h-4 w-4 mr-2" />
                Add Division
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {!selectedSport ? (
            <div className="py-8 text-center text-muted-foreground">Select a sport to view divisions</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[560px]">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-3 px-4">Name</th>
                    <th className="text-left py-3 px-4">Gender</th>
                    <th className="text-left py-3 px-4">Team Size</th>
                    <th className="text-left py-3 px-4">Status</th>
                    {isAdmin && <th className="text-right py-3 px-4">Actions</th>}
                  </tr>
                </thead>
                <tbody>
                  {divisionsLoading ? (
                    <tr>
                      <td colSpan={isAdmin ? 5 : 4} className="py-8 text-center text-muted-foreground">
                        Loading divisions...
                      </td>
                    </tr>
                  ) : divisions.length === 0 ? (
                    <tr>
                      <td colSpan={isAdmin ? 5 : 4} className="py-8 text-center text-muted-foreground">
                        No divisions found
                      </td>
                    </tr>
                  ) : (
                    divisions.map((d) => (
                      <tr key={d.id} className="border-b hover:bg-muted/50">
                        <td className="py-3 px-4 font-medium">{d.name}</td>
                        <td className="py-3 px-4 capitalize">{d.gender}</td>
                        <td className="py-3 px-4">{d.team_size}</td>
                        <td className="py-3 px-4">
                          {isAdmin ? (
                            <div className="max-w-[140px]">
                              <Select
                                value={d.is_active ? 'active' : 'inactive'}
                                onValueChange={(value) => onSetDivisionActive(d, value === 'active')}
                              >
                                <SelectTrigger className="h-8">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="active">Active</SelectItem>
                                  <SelectItem value="inactive">Inactive</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                          ) : (
                            statusBadge(!!d.is_active)
                          )}
                        </td>
                        {isAdmin && (
                          <td className="py-3 px-4">
                            <div className="flex items-center justify-end gap-2">
                              <Button variant="ghost" size="sm" onClick={() => onOpenEditDivision(d)}>
                                <Edit className="h-4 w-4" />
                              </Button>
                              <Button variant="ghost" size="sm" onClick={() => onOpenDeleteDivision(d)}>
                                <Trash2 className="h-4 w-4 text-destructive" />
                              </Button>
                            </div>
                          </td>
                        )}
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}

          {!isAdmin && (
            <p className="text-xs text-muted-foreground mt-4">
              Division management is restricted to administrators.
            </p>
          )}
        </CardContent>
      </Card>

      {/* Add Division Modal */}
      {showAddDivisionModal && selectedSport && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <Card className="w-full max-w-md max-h-[90vh] overflow-y-auto">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Add Division</CardTitle>
                <Button variant="ghost" size="icon" onClick={onCloseAddDivisionModal}>
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium mb-2 block">Name *</label>
                  <Input
                    value={divisionForm.name}
                    onChange={(e) => onDivisionFormChange({ ...divisionForm, name: e.target.value })}
                    placeholder="Men's Singles"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium mb-2 block">Description</label>
                  <Input
                    value={divisionForm.description}
                    onChange={(e) => onDivisionFormChange({ ...divisionForm, description: e.target.value })}
                    placeholder="Optional description"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium mb-2 block">Gender *</label>
                  <Select
                    value={divisionForm.gender}
                    onValueChange={(val) => onDivisionFormChange({ ...divisionForm, gender: val })}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Select gender" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="male">Male</SelectItem>
                      <SelectItem value="female">Female</SelectItem>
                      <SelectItem value="mixed">Mixed</SelectItem>
                      <SelectItem value="team">Team</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-sm font-medium mb-2 block">Team Size *</label>
                  <Input
                    type="number"
                    value={divisionForm.team_size}
                    onChange={(e) => onDivisionFormChange({ ...divisionForm, team_size: e.target.value })}
                    min={1}
                  />
                </div>
                <div className="flex items-center gap-2">
                  <input
                    id="division-active"
                    type="checkbox"
                    checked={divisionForm.is_active}
                    onChange={(e) => onDivisionFormChange({ ...divisionForm, is_active: e.target.checked })}
                  />
                  <label htmlFor="division-active" className="text-sm">Active</label>
                </div>
              </div>

              <div className="flex gap-2 pt-4 mt-4 border-t">
                <Button variant="outline" onClick={onCloseAddDivisionModal} className="flex-1">
                  Cancel
                </Button>
                <Button onClick={() => onSaveDivision('add')} className="flex-1" disabled={!divisionForm.name}>
                  Create
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Edit Division Modal */}
      {showEditDivisionModal && editingDivision && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <Card className="w-full max-w-md max-h-[90vh] overflow-y-auto">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Edit Division</CardTitle>
                <Button variant="ghost" size="icon" onClick={onCloseEditDivisionModal}>
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium mb-2 block">Name *</label>
                  <Input
                    value={divisionForm.name}
                    onChange={(e) => onDivisionFormChange({ ...divisionForm, name: e.target.value })}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium mb-2 block">Description</label>
                  <Input
                    value={divisionForm.description}
                    onChange={(e) => onDivisionFormChange({ ...divisionForm, description: e.target.value })}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium mb-2 block">Gender *</label>
                  <Select
                    value={divisionForm.gender}
                    onValueChange={(val) => onDivisionFormChange({ ...divisionForm, gender: val })}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Select gender" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="male">Male</SelectItem>
                      <SelectItem value="female">Female</SelectItem>
                      <SelectItem value="mixed">Mixed</SelectItem>
                      <SelectItem value="team">Team</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-sm font-medium mb-2 block">Team Size *</label>
                  <Input
                    type="number"
                    value={divisionForm.team_size}
                    onChange={(e) => onDivisionFormChange({ ...divisionForm, team_size: e.target.value })}
                    min={1}
                  />
                </div>
                <div className="flex items-center gap-2">
                  <input
                    id="division-active-edit"
                    type="checkbox"
                    checked={divisionForm.is_active}
                    onChange={(e) => onDivisionFormChange({ ...divisionForm, is_active: e.target.checked })}
                  />
                  <label htmlFor="division-active-edit" className="text-sm">Active</label>
                </div>
              </div>

              <div className="flex gap-2 pt-4 mt-4 border-t">
                <Button variant="outline" onClick={onCloseEditDivisionModal} className="flex-1">
                  Cancel
                </Button>
                <Button onClick={() => onSaveDivision('edit')} className="flex-1" disabled={!divisionForm.name}>
                  Save
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Delete Division Modal */}
      {showDeleteDivisionModal && deletingDivision && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <Card className="w-full max-w-md">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Delete Division</CardTitle>
                <Button variant="ghost" size="icon" onClick={onCloseDeleteDivisionModal}>
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground">
                Are you sure you want to delete <span className="font-semibold">{deletingDivision.name}</span>? This cannot be undone.
              </p>
              <div className="flex gap-2 pt-4 mt-4 border-t">
                <Button variant="outline" onClick={onCloseDeleteDivisionModal} className="flex-1">
                  Cancel
                </Button>
                <Button variant="destructive" onClick={onConfirmDeleteDivision} className="flex-1">
                  Delete
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </>
  )
}
